"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Key } from "react";
import { DataGrid, type SortDirection } from "@xefi/x-react/datagrid";
import { useTranslation } from "react-i18next";
import type { MissingCardPaymentModel } from "../../../../domain/models/MissingCardPayment";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useSearchPending } from "@/hooks/useSearchPending";
import { useMissingCardPayments } from "@/hooks/useMissingCardPayments";
import { usePaymentsDateRange, usePaymentsDomaIds, usePaymentsSubsidiaryId } from "@/store/usePaymentsFiltersStore";
import { usePaymentsSearchStore, usePaymentsSearchTerm } from "@/store/usePaymentsSearchStore";
import type { MissingCardPaymentState, PaymentsDoneState, PaymentsSortConfig } from "@/types/payments";
import { isForbiddenError } from "@/utils/apiError";
import ExportButton from "../common/ExportButton";
import PaymentsDoneFilter from "../common/PaymentsDoneFilter";
import PaymentsSearchInput from "../common/PaymentsSearchInput";
import { getSharedFiltersSignature, stateToScopes, toDoneFilters, toSearchFilter, toTableFilters } from "../common/paymentsQuery";
import HistoryDrawer from "../common/HistoryDrawer";
import SegmentedControl from "../common/SegmentedControl";
import { type EditableField, MissingCardPaymentRowEditor } from "./MissingCardPaymentRowEditor";
import { useMissingCardPaymentsColumns } from "./missingCardPaymentsColumns";
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const DEFAULT_ROWS_PER_PAGE = 25;
const SEARCH_FIELDS = ["label", "ref_key_1", "ref_key_2", "ref_key_3", "functional_doma", "assignment", "comment"];
const AMOUNT_SEARCH_FIELDS = ["amount"];
type Props = {
  fixedState?: MissingCardPaymentState;
  showExport?: boolean;
};
export default function MissingCardPaymentsTable(t0) {
  const $ = _c(100);
  const {
    fixedState,
    showExport: t1
  } = t0;
  const showExport = t1 === undefined ? true : t1;
  const {
    t
  } = useTranslation();
  const subsidiaryId = usePaymentsSubsidiaryId();
  const domaIds = usePaymentsDomaIds();
  const dateRange = usePaymentsDateRange();
  const [internalState, setInternalState] = useState("in_progress");
  const [done, setDone] = useState("all");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sort, setSort] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingField, setEditingField] = useState("assignment");
  const anchorRef = useRef(null);
  const state = fixedState ?? internalState;
  const search = usePaymentsSearchTerm("missing_card_payments");
  const setSearchTerm = usePaymentsSearchStore(_temp);
  const debouncedSearch = useDebouncedValue(search);
  const activeSearch = fixedState ? "" : debouncedSearch;
  let searchFilter;
  let t2;
  if ($[0] !== activeSearch || $[1] !== dateRange || $[2] !== domaIds || $[3] !== done || $[4] !== subsidiaryId) {
    const base = toTableFilters({
      subsidiaryId,
      domaIds,
      dateRange
    }, {
      field: "document_date",
      kind: "date"
    });
    let t3;
    if ($[7] !== activeSearch) {
      t3 = toSearchFilter(activeSearch, SEARCH_FIELDS, AMOUNT_SEARCH_FIELDS);
      $[7] = activeSearch;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    searchFilter = t3;
    t2 = [...base, ...toDoneFilters(done)];
    $[0] = activeSearch;
    $[1] = dateRange;
    $[2] = domaIds;
    $[3] = done;
    $[4] = subsidiaryId;
    $[5] = searchFilter;
    $[6] = t2;
  } else {
    searchFilter = $[5];
    t2 = $[6];
  }
  const scoped = t2;
  let t3;
  if ($[9] !== scoped || $[10] !== searchFilter) {
    t3 = searchFilter ? [...scoped, searchFilter] : scoped;
    $[9] = scoped;
    $[10] = searchFilter;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const filters = t3;
  let t4;
  if ($[12] !== state) {
    t4 = stateToScopes(state);
    $[12] = state;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const scopes = t4;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setEditingRowId(null);
    };
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleCloseEditor = t5;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (row, field, anchor) => {
      anchorRef.current = anchor;
      setEditingField(field);
      setEditingRowId(row.id);
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleOpenEditor = t6;
  let t7;
  if ($[16] !== dateRange || $[17] !== domaIds || $[18] !== subsidiaryId) {
    t7 = getSharedFiltersSignature({
      subsidiaryId,
      domaIds,
      dateRange
    });
    $[16] = dateRange;
    $[17] = domaIds;
    $[18] = subsidiaryId;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const filtersSignature = `${t7}|${activeSearch}|${done}`;
  const [previousSignature, setPreviousSignature] = useState(filtersSignature);
  if (filtersSignature !== previousSignature) {
    setPreviousSignature(filtersSignature);
    setPage(1);
    setEditingRowId(null);
  }
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      onOpenEditor: handleOpenEditor,
      onOpenHistory: setHistoryRow
    };
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const columns = useMissingCardPaymentsColumns(t8);
  let t9;
  if ($[21] !== sort) {
    t9 = sort ? [sort] : undefined;
    $[21] = sort;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== filters || $[24] !== page || $[25] !== rowsPerPage || $[26] !== scopes || $[27] !== t9) {
    t10 = {
      filters,
      scopes,
      sorts: t9,
      page,
      limit: rowsPerPage
    };
    $[23] = filters;
    $[24] = page;
    $[25] = rowsPerPage;
    $[26] = scopes;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const {
    data,
    total,
    isLoading,
    isFetching,
    error
  } = useMissingCardPayments(t10);
  const isSearching = useSearchPending(activeSearch, isFetching);
  let t11;
  if ($[29] !== data || $[30] !== editingRowId) {
    t11 = editingRowId !== null ? data.find(row_0 => row_0.id === editingRowId) ?? null : null;
    $[29] = data;
    $[30] = editingRowId;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const editingRow = t11;
  let t12;
  let t13;
  if ($[32] !== editingRow || $[33] !== editingRowId) {
    t12 = () => {
      if (editingRowId !== null && editingRow === null) {
        setEditingRowId(null);
      }
    };
    t13 = [editingRowId, editingRow];
    $[32] = editingRow;
    $[33] = editingRowId;
    $[34] = t12;
    $[35] = t13;
  } else {
    t12 = $[34];
    t13 = $[35];
  }
  useEffect(t12, t13);
  let t14;
  if ($[36] !== t) {
    t14 = t("paymentsReceived.state.inProgress");
    $[36] = t;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t14) {
    t15 = {
      key: "in_progress",
      label: t14
    };
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t) {
    t16 = t("paymentsReceived.state.archived");
    $[40] = t;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t16) {
    t17 = {
      key: "archived",
      label: t16
    };
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t) {
    t18 = t("paymentsReceived.state.all");
    $[44] = t;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t18) {
    t19 = {
      key: "all",
      label: t18
    };
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t15 || $[49] !== t17 || $[50] !== t19) {
    t20 = [t15, t17, t19];
    $[48] = t15;
    $[49] = t17;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  const stateOptions = t20;
  let t21;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = nextState => {
      handleCloseEditor();
      setInternalState(nextState as MissingCardPaymentState);
      setPage(1);
    };
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  const handleStateChange = t21;
  let t22;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = nextDone => {
      handleCloseEditor();
      setDone(nextDone);
      setPage(1);
    };
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const handleDoneChange = t22;
  let t23;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = (column, direction) => {
      handleCloseEditor();
      setSort({
        field: String(column),
        direction
      });
      setPage(1);
    };
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  const handleSortChange = t23;
  let t24;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = nextRowsPerPage => {
      handleCloseEditor();
      setRowsPerPage(nextRowsPerPage);
      setPage(1);
    };
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  const handleRowsPerPageChange = t24;
  let t25;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = nextPage => {
      handleCloseEditor();
      setPage(nextPage);
    };
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  const handleFetchPage = t25;
  let t26;
  if ($[57] !== fixedState || $[58] !== state || $[59] !== stateOptions || $[60] !== t) {
    t26 = fixedState ? <span /> : <SegmentedControl ariaLabel={t("paymentsReceived.state.inProgress")} options={stateOptions} value={state} onChange={handleStateChange} />;
    $[57] = fixedState;
    $[58] = state;
    $[59] = stateOptions;
    $[60] = t;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== done || $[63] !== fixedState || $[64] !== search || $[65] !== setSearchTerm) {
    t27 = !fixedState && <><PaymentsDoneFilter value={done} onChange={handleDoneChange} /><PaymentsSearchInput value={search} onChange={value => setSearchTerm("missing_card_payments", value)} /></>;
    $[62] = done;
    $[63] = fixedState;
    $[64] = search;
    $[65] = setSearchTerm;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== done || $[68] !== showExport) {
    t28 = showExport && <ExportButton category="missing_card_payments" done={done} />;
    $[67] = done;
    $[68] = showExport;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== t27 || $[71] !== t28) {
    t29 = <div className="flex flex-wrap items-center gap-2">{t27}{t28}</div>;
    $[70] = t27;
    $[71] = t28;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== t26 || $[74] !== t29) {
    t30 = <div className="flex flex-wrap items-center justify-between gap-2">{t26}{t29}</div>;
    $[73] = t26;
    $[74] = t29;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== columns || $[77] !== data || $[78] !== error || $[79] !== isFetching || $[80] !== isLoading || $[81] !== isSearching || $[82] !== page || $[83] !== rowsPerPage || $[84] !== t || $[85] !== total) {
    t31 = error ? <div className="flex h-[24vh] items-center justify-center rounded-xl border border-border dark:border-[#3a3a3a]"><p className="text-sm text-danger">{isForbiddenError(error) ? t("paymentsReceived.states.forbidden") : t("paymentsReceived.states.error")}</p></div> : <DataGrid className="w-full" rows={data} columns={columns} isLoading={isLoading || isSearching} isFetching={isFetching} childrenProps={{
      tableBodyProps: {
        emptyContent: <div className="flex h-[18vh] items-center justify-center"><p className="text-lg text-[#666666] dark:text-[#d1cfcf]">{t("paymentsReceived.missingCardPayments.empty")}</p></div>
      }
    }} onSortChange={handleSortChange} paginationType="paginated" currentPage={page} totalItems={total} rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS} defaultRowsPerPage={rowsPerPage} onFetchPage={handleFetchPage} onRowsPerPageChange={handleRowsPerPageChange} />;
    $[76] = columns;
    $[77] = data;
    $[78] = error;
    $[79] = isFetching;
    $[80] = isLoading;
    $[81] = isSearching;
    $[82] = page;
    $[83] = rowsPerPage;
    $[84] = t;
    $[85] = total;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  let t32;
  if ($[87] !== editingField || $[88] !== editingRow) {
    t32 = editingRow !== null && <MissingCardPaymentRowEditor key={`${editingRow.id}-${editingField}`} row={editingRow} field={editingField} anchorRef={anchorRef} onClose={handleCloseEditor} />;
    $[87] = editingField;
    $[88] = editingRow;
    $[89] = t32;
  } else {
    t32 = $[89];
  }
  const t33 = historyRow !== null;
  let t34;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => setHistoryRow(null);
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  const t35 = historyRow?.activities;
  let t36;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = ["assignment", "to_do", "comment"];
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  let t37;
  if ($[92] !== t33 || $[93] !== t35) {
    t37 = <HistoryDrawer isOpen={t33} onClose={t34} activities={t35} trackedFields={t36} />;
    $[92] = t33;
    $[93] = t35;
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  let t38;
  if ($[95] !== t30 || $[96] !== t31 || $[97] !== t32 || $[98] !== t37) {
    t38 = <div className="flex flex-col gap-3">{t30}{t31}{t32}{t37}</div>;
    $[95] = t30;
    $[96] = t31;
    $[97] = t32;
    $[98] = t37;
    $[99] = t38;
  } else {
    t38 = $[99];
  }
  return t38;
}
function _temp(store) {
  return store.setTerm;
}